import { Component, type ErrorInfo, type ReactNode } from 'react';
import { defaultLanguage, translations } from '../i18n/translations';
import { Sentry } from '../lib/sentry';
import { colors } from '../theme/colors';
import { tokens } from '../theme/tokens';
import { darkPrecisionFontFamilies } from '../theme/typography';
import { Box } from './primitives/Box';
import { Button } from './primitives/Button';
import { Text } from './primitives/Text';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.withScope((scope: { setContext: (key: string, value: unknown) => void }) => {
      scope.setContext('errorBoundary', {
        componentStack: errorInfo.componentStack,
      });
      Sentry.captureException(error);
    });

    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    if (this.state.hasError) {
      const t = translations[defaultLanguage];
      const components = t.components as Record<string, Record<string, string>>;
      const common = t.common as Record<string, Record<string, string>>;

      return (
        <Box
          flex={1}
          backgroundColor={colors.background.primary}
          justifyContent="center"
          alignItems="center"
          padding="xl"
        >
          <Box alignItems="center" maxWidth={400}>
            <Text size="5xl" align="center" style={{ marginBottom: 16 }}>
              😔
            </Text>
            <Text
              variant="h3"
              weight="bold"
              color={colors.text.primary}
              align="center"
              style={{ marginBottom: 12 }}
            >
              {components.errorBoundary.title}
            </Text>
            <Text
              variant="body"
              color={colors.text.secondary}
              align="center"
              style={{ marginBottom: 24, lineHeight: 24 }}
            >
              {components.errorBoundary.description}
            </Text>

            {process.env.NODE_ENV !== 'production' && this.state.error && (
              <Box
                backgroundColor={colors.surface.row}
                padding="lg"
                borderRadius="md"
                width="100%"
                style={{ marginBottom: tokens.spacing.xl }}
              >
                <Text
                  size="base"
                  weight="bold"
                  color={colors.status.error.light}
                  style={{ marginBottom: tokens.spacing.sm }}
                >
                  {components.errorBoundary.devError}
                </Text>
                <Text
                  size="sm"
                  color={colors.status.error.light}
                  style={{
                    fontFamily: darkPrecisionFontFamilies.metric,
                    marginBottom: tokens.spacing.sm,
                  }}
                >
                  {this.state.error.toString()}
                </Text>
                {this.state.error.stack && (
                  <Text
                    size="xs"
                    color={colors.text.tertiary}
                    style={{ fontFamily: darkPrecisionFontFamilies.metric }}
                  >
                    {this.state.error.stack}
                  </Text>
                )}
              </Box>
            )}

            <Button variant="primary" onPress={this.handleReset}>
              {common.actions.tryAgain}
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}
